import { useEffect, useState } from "react";

// The browser's own speech synthesis, as a stand-in for generated audio: used
// while a deck's MP3s haven't been generated yet, or if one fails to load.
// Quality varies by device, so it's the fallback, never the first choice.

function synth() {
  return typeof window !== "undefined" && window.speechSynthesis ? window.speechSynthesis : null;
}

// Every voice on this device for the language, best first: the exact locale
// ("pt-PT" before "pt-BR"), then voices that run on the device (no network).
export function matchingVoices(voices, lang) {
  if (!lang || !voices || !voices.length) return [];

  const wanted = lang.toLowerCase().replace("_", "-");
  const base = wanted.split("-")[0];
  const normal = (voice) => (voice.lang || "").toLowerCase().replace("_", "-");
  const score = (voice) => (normal(voice) === wanted ? 0 : 2) + (voice.localService ? 0 : 1);

  return voices
    .filter((voice) => normal(voice).split("-")[0] === base)
    .sort((a, b) => score(a) - score(b));
}

export function findVoice(voices, lang) {
  return matchingVoices(voices, lang)[0] || null;
}

// Voices load asynchronously in Chrome - getVoices() is empty until the
// voiceschanged event - so this re-checks when they arrive.
export function useBrowserVoices(lang) {
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    const speech = synth();
    if (!speech || !lang) return undefined;

    const update = () => setVoices(matchingVoices(speech.getVoices(), lang));
    update();
    speech.addEventListener("voiceschanged", update);
    return () => speech.removeEventListener("voiceschanged", update);
  }, [lang]);

  return voices;
}

// Resolves when the browser has finished speaking (or straight away if it
// can't), so "play both" can follow it with the learner's recording.
export function speak(text, voice) {
  const speech = synth();
  if (!speech || !voice || !text) return Promise.resolve();

  return new Promise((resolve) => {
    speech.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.voice = voice;
    utterance.lang = voice.lang;
    // A touch slower than default: these are phrases to imitate, not skim.
    utterance.rate = 0.9;
    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    speech.speak(utterance);
  });
}
